import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface StockMovement {
  id?: number;
  productCode: string;
  productName: string;
  movementType: 'IN' | 'OUT';
  quantity: number;
  unitPrice: number;
  totalValue: number;
  referenceNumber: string;
  movementDate: string;
  location: string;
  performedBy: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StockMovementStats {
  totalMovements: number;
  totalIn: number;
  totalOut: number;
  totalValue: number;
  averageMovementValue: number;
  topProducts: {
    productCode: string;
    productName: string;
    totalMovements: number;
    totalValue: number;
  }[];
  locationBreakdown: {
    location: string;
    movementsCount: number;
    totalValue: number;
  }[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class StockMovementsService {
  private apiUrl = `${environment.apiServiceBaseUrl}/hawdco/stock-movements`;

  constructor(private http: HttpClient) {}

  // Get all stock movements with optional pagination and filtering
  getStockMovements(
    page: number = 1, 
    limit: number = 10, 
    search?: string, 
    movementType?: string,
    location?: string,
    dateFrom?: string,
    dateTo?: string
  ): Observable<ApiResponse<StockMovement[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) params = params.set('search', search);
    if (movementType) params = params.set('movementType', movementType);
    if (location) params = params.set('location', location);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get<ApiResponse<StockMovement[]>>(this.apiUrl, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get stock movement by ID
  getStockMovementById(id: number): Observable<ApiResponse<StockMovement>> {
    return this.http.get<ApiResponse<StockMovement>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Create new stock movement
  createStockMovement(movement: StockMovement): Observable<ApiResponse<StockMovement>> {
    return this.http.post<ApiResponse<StockMovement>>(this.apiUrl, movement)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update stock movement
  updateStockMovement(id: number, movement: StockMovement): Observable<ApiResponse<StockMovement>> {
    return this.http.put<ApiResponse<StockMovement>>(`${this.apiUrl}/${id}`, movement)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Delete stock movement
  deleteStockMovement(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get stock movements statistics
  getStockMovementsStats(dateFrom?: string, dateTo?: string): Observable<ApiResponse<StockMovementStats>> {
    let params = new HttpParams();
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get<ApiResponse<StockMovementStats>>(`${this.apiUrl}/stats`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get available locations
  getLocations(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/hawdco/locations`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get movement types
  getMovementTypes(): Observable<ApiResponse<string[]>> {
    return this.http.get<ApiResponse<string[]>>(`${this.apiUrl}/hawdco/movement-types`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Validate stock movement before creating
  validateStockMovement(movement: StockMovement): Observable<ApiResponse<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    currentStock: number;
    projectedStock: number;
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/hawdco/validate`, movement)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Bulk create stock movements
  bulkCreateStockMovements(movements: StockMovement[]): Observable<ApiResponse<{
    successful: StockMovement[];
    failed: {
      movement: StockMovement;
      error: string;
    }[];
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/hawdco/bulk`, { movements })
      .pipe(
        catchError(this.handleError)
      );
  }

  // Export stock movements to Excel
  exportToExcel(search?: string, movementType?: string, location?: string, dateFrom?: string, dateTo?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (movementType) params = params.set('movementType', movementType);
    if (location) params = params.set('location', location);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get(`${this.apiUrl}/export/excel`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Export stock movements to PDF
  exportToPDF(search?: string, movementType?: string, location?: string, dateFrom?: string, dateTo?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (movementType) params = params.set('movementType', movementType);
    if (location) params = params.set('location', location);
    if (dateFrom) params = params.set('dateFrom', dateFrom);
    if (dateTo) params = params.set('dateTo', dateTo);

    return this.http.get(`${this.apiUrl}/export/pdf`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get stock movements by reference number
  getMovementsByReference(referenceNumber: string): Observable<ApiResponse<StockMovement[]>> {
    return this.http.get<ApiResponse<StockMovement[]>>(`${this.apiUrl}/hawdco/reference/${referenceNumber}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get stock movements by product
  getMovementsByProduct(productCode: string, limit: number = 50): Observable<ApiResponse<StockMovement[]>> {
    return this.http.get<ApiResponse<StockMovement[]>>(`${this.apiUrl}/hawdco/product/${productCode}?limit=${limit}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('Stock Movements API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
